import { BaseService } from '@services/BaseService';
import {
  PurchaseRequestViewModel,
  PurchaseRequestSearchViewModel,
  // PurchaseRequestInfoViewModel,
  PurchasingRequestStatusesViewModel,
  Accessory
} from '@models/PurchaseRequest';
import { PageViewModel, OptionsViewModel, StatesViewModel } from '@models/common.models';
import { CILReasonViewModel } from '@models/CILReason';

class SwoPurchasingService extends BaseService {
  constructor() {
    super('swo/purchasing');
  }

  getPurchaseRequest(purchaseRequestId: number): Promise<PurchaseRequestViewModel> {
    return this.fetch<PurchaseRequestViewModel>(`${purchaseRequestId}`, {
      method: 'GET'
    });
  }

  createPurchaseRequest(purchaseRequest: PurchaseRequestViewModel) {
    return this.fetch('', {
      method: 'POST',
      body: JSON.stringify(purchaseRequest)
    });
  }

  getAccessories(cviCd: number): Promise<Accessory[]> {
    return this.fetch<Accessory[]>(`cvis/${cviCd}/accessories`, {
      method: 'GET'
    });
  }

  getSuppliers(isHvac: boolean): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`suppliers?isHvac=${isHvac}`, {
      method: 'GET'
    });
  }

  getStatuses(): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`statuses`, {
      method: 'GET'
    });
  }

  getFacilities(supplierId: number, stateAbbreviation?: string): Promise<OptionsViewModel[]> {
    let stateAbbr = stateAbbreviation && stateAbbreviation.length > 0 ? `?stateAbbr=${stateAbbreviation}` : '';
    return this.fetch<OptionsViewModel[]>(`supplier/${supplierId}/facilities${stateAbbr}`, {
      method: 'GET'
    });
  }

  getColors(): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`colors`, {
      method: 'GET'
    });
  }

  getFacilityStates() {
    return this.fetch<StatesViewModel[]>('facilityStates', {
      method: 'GET'
    });
  }

  getFuel() {
    return this.fetch<OptionsViewModel[]>('fuel', {
      method: 'GET'
    });
  }

  savePurchaseRequest(purchaseRequestId: number, purchaseRequest: PurchaseRequestViewModel) {
    return this.fetch(`${purchaseRequestId}`, {
      method: 'PUT',
      body: JSON.stringify(purchaseRequest)
    });
  }

  getPurchaseRequestStatuses(): Promise<PurchasingRequestStatusesViewModel> {
    return this.fetch<PurchasingRequestStatusesViewModel>('purchaseRequestStatuses', {
      method: 'GET'
    });
  }

  searchPurchaseRequests(
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    includeClosed: boolean
  ) {
    let queryParameters = [];

    // Attach query
    if (query) {
      queryParameters.push(`query=${query}`);
    }

    // Handle Paging
    queryParameters.push(`pageNumber=${Math.max(1, pageNumber)}`);
    queryParameters.push(`pageSize=${Math.max(0, pageSize)}`);
    // Handle Sorting
    if (orderBy) {
      queryParameters.push(`orderBy=${encodeURIComponent(orderBy)}`);
    }
    if (orderDirection) {
      queryParameters.push(`orderDirection=${encodeURIComponent(orderDirection)}`);
    }

    if (includeClosed) {
      queryParameters.push(`includeClosed=${includeClosed}`);
    }

    const uri: string = 'purchasingRequests'.concat(queryParameters.length > 0 ? '?' : '', queryParameters.join('&'));

    return this.fetch<PageViewModel<PurchaseRequestSearchViewModel>>(uri, {
      method: 'GET'
    });
  }

  reopenPurchaseRequest(purchaseRequestId: number) {
    return this.fetch(`${purchaseRequestId}/reopen`, {
      method: 'POST'
    });
  }

  completePurchaseRequest(purchaseRequestId: number) {
    return this.fetch(`${purchaseRequestId}/complete`, {
      method: 'POST'
    });
  }

  changePurchasingOwnership(id: number, release?: boolean): Promise<PurchaseRequestSearchViewModel> {
    if (release) {
      return this.fetch(`${id}/release`, {
        method: 'POST'
      });
    } else {
      return this.fetch(`${id}/own`, {
        method: 'POST'
      });
    }
  }

  getCILReasons(): Promise<CILReasonViewModel[]> {
    return this.fetch<CILReasonViewModel[]>(`cilReasons`, {
      method: 'GET'
    });
  }

  getPurchaseRequestTypes() {
    return this.fetch<OptionsViewModel[]>(`requestTypes`, {
      method: 'GET'
    });
  }

  getHvacSystemTypes(supplierId: number): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`supplier/${supplierId}/hvacSystemTypes`, {
      method: 'GET'
    });
  }

  getHvacParts(systemTypeId: number): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`hvacSystemTypes/${systemTypeId}/hvacParts`, {
      method: 'GET'
    });
  }

  getHvacVendorLocations(supplierId: number): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`supplier/${supplierId}/hvacVendorLocations`, {
      method: 'GET'
    });
  }

  savePurchaseRequestHvac(purchaseRequestId: number, purchaseRequest: PurchaseRequestViewModel) {
    return this.fetch(`${purchaseRequestId}/hvac`, {
      method: 'PUT',
      body: JSON.stringify(purchaseRequest)
    });
  }

  savePurchaseRequestParts(purchaseRequestId: number, purchaseRequest: PurchaseRequestViewModel) {
    return this.fetch(`${purchaseRequestId}/parts`, {
      method: 'PUT',
      body: JSON.stringify(purchaseRequest)
    });
  }
}

export const swoPurchasingService = new SwoPurchasingService();



// WEBPACK FOOTER //
// ./src/_services/SwoPurchasingService.ts